/* eslint-disable array-callback-return */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";

// @material-ui/core components
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import LinearProgress from "@material-ui/core/LinearProgress";
import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Create from "@material-ui/icons/Create";
import Check from "@material-ui/icons/Check";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

// @material-ui/lab components
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles } from "@material-ui/core/styles";

// Karsten
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import TransferList from "components/List/TransferList";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import useStructureQueries from "hooks/useStructureQueries";
import InputText from "components/_refactor/text/input";
import PasswordField from "components/_refactor/fields/password";
import UserFormStepper from "views/admin/components/user-form-stepper";
import UserAccessSection from "views/admin/components/user-access-section";
import {
  aggregateProfileAccess,
  createEmptyPeopleByType,
  formatProfileAccessSummary,
  getCanaisPerfilParam,
  getVisibleAccessTabs,
  isInternoKsaOnly,
  mergePeopleByType,
  validateAccessStep,
} from "views/admin/utils/user-profile-access-helpers";
import {
  getProfileOptionLabel,
  PROFILE_CANAL_OPTIONS,
  PROFILE_TIPO_PESSOA_OPTIONS,
} from "views/admin/constants/profile-options";
import {
  userHasAnyProfileName,
  userIsRepresentante,
} from "utils/profile-classification";

const formatPersonDocument = (document) => {
  if (!document) {
    return "";
  }

  if (document.length === 14) {
    return document.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5",
    );
  }

  return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "$1.$2.$3-$4");
};

const mapPersonToListItem = (person) => ({
  Id: person.Id,
  Nome: person.Nome,
  Cnpj: formatPersonDocument(person.CNPJ || person.CnpjCpfPessoa),
  Apelido: person.Apelido,
});

const mapPessoaDtoToListItem = (person) => ({
  Id: person.IdPessoa,
  Nome: person.Nome,
  Cnpj: formatPersonDocument(person.CnpjCpfPessoa),
  Apelido: person.Apelido,
});

const splitLinkedPeopleByType = (people = []) => {
  const lojistas = [];
  const representantes = [];

  people.forEach((person) => {
    const item = mapPessoaDtoToListItem(person);

    if (person.ERepresentante) {
      representantes.push(item);
    }

    if (person.ELojista || !person.ERepresentante) {
      lojistas.push(item);
    }
  });

  return {
    LOJISTA: lojistas,
    REPRESENTANTE: representantes,
  };
};

const ACCESS_PAGE_SIZE = 100;
const BRAND_FILTER_ALL = "TODAS";

const normalizeSearchText = (text) => (text || "").replace(/\D/g, "").toLowerCase();

const matchesPersonSearchFilter = (person, filter) => {
  const trimmedFilter = (filter || "").trim();

  if (!trimmedFilter) {
    return true;
  }

  const normalizedFilter = normalizeSearchText(trimmedFilter);
  const lowerFilter = trimmedFilter.toLowerCase();
  const searchableFields = [
    person.Nome,
    person.Apelido,
    person.Cnpj,
    person.CNPJ,
    person.CnpjCpfPessoa,
  ].filter(Boolean);

  return searchableFields.some((field) => {
    const fieldText = String(field);

    if (fieldText.toLowerCase().includes(lowerFilter)) {
      return true;
    }

    return normalizeSearchText(fieldText).includes(normalizedFilter);
  });
};

const arePeopleListsEqual = (left, right) => {
  if (!Array.isArray(left) || !Array.isArray(right)) {
    return left === right;
  }

  if (left.length !== right.length) {
    return false;
  }

  const rightIds = new Set(right.map((person) => person.Id));

  return left.every((person) => rightIds.has(person.Id));
};

const useCustomStyles = makeStyles((theme) => ({
  isAdminStyle: {
    display: "none",
  },
  disableInput: {
    "& input.Mui-disabled": {
      backgroundColor: "rgb(240, 240, 240)",
    },
  },
}));

const filtraNaoVinculados = (items, vinculados, fieldKey) => {
  const naoVinculados = (items || []).filter(
    (field) =>
      !vinculados.some((vinculado) => vinculado[fieldKey] === field[fieldKey]),
  );
  return naoVinculados;
};

const checkNothingChanged = (
  selectedUser,
  perfisVinculados,
  canaisVinculados,
  initialPessoasVinculados,
  pessoasVinculados,
  userPassword,
) => {
  return (
    !selectedUser ||
    !(
      (perfisVinculados &&
        !UtilsFncs.ArrayEquals(selectedUser.UsuarioPerfis, perfisVinculados)) ||
      (canaisVinculados &&
        !UtilsFncs.ArrayEquals(selectedUser.UsuarioCanais, canaisVinculados)) ||
      (pessoasVinculados &&
        !UtilsFncs.ArrayEquals(initialPessoasVinculados, pessoasVinculados)) ||
      (userPassword && userPassword?.length !== 0)
    )
  );
};

export default function FormUserDialog({
  open,
  formType,
  onClose,
  selectedUser,
  loadingDetails = false,
}) {
  const classes = useCustomStyles();
  const { getChannels, getBrands, getRepresentatives } = useStructureQueries();
  const [isAdmin, setIsAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [channels, setChannels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [people, setPeople] = useState([]);
  const [accessPersonType, setAccessPersonType] = useState("REPRESENTANTE");

  const [preSelectedProfiles, setPreSelectedProfiles] = useState(
    selectedUser?.UsuarioPerfis || [],
  );
  const [preSelectedChannels, setPreSelectedChannels] = useState(
    selectedUser?.UsuarioCanais || [],
  );
  const [preSelectedPeople, setPreSelectedPeople] = useState(
    selectedUser?.UsuarioPessoas || [],
  );

  const [totalPage, setTotalPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRegistros, setTotalRegistros] = useState(null);
  const [filterStore, setFilterStore] = useState(null);
  const [linkedCurrentPage, setLinkedCurrentPage] = useState(0);
  const [linkedTotalPage, setLinkedTotalPage] = useState(null);
  const [linkedTotalRegistros, setLinkedTotalRegistros] = useState(null);
  const [filterLinkedStore, setFilterLinkedStore] = useState(null);
  const [linkedPeoplePage, setLinkedPeoplePage] = useState([]);
  const [loadingLinkedPeople, setLoadingLinkedPeople] = useState(false);
  const [hasAccessChanges, setHasAccessChanges] = useState(false);
  const [isRepresentativeSelected, setIsRepresentativeSElected] =
    useState(false);
  const [perfisVinculados, setPerfisVinculados] = useState(
    selectedUser?.UsuarioPerfis || [],
  );
  const [canaisVinculados, setCanaisVinculados] = useState(
    selectedUser?.UsuarioCanais || [],
  );
  const [brandFilter, setBrandFilter] = useState(BRAND_FILTER_ALL);
  const [pessoasVinculados, setPessoasVinculados] = useState([]);
  const [pessoasByType, setPessoasByType] = useState(createEmptyPeopleByType());
  const [initialPessoasVinculados, setInitialPessoasVinculados] =
    useState(null);
  const [disablePasswordInput, setDisablePasswordInput] = useState(
    formType === "edit" && true,
  );
  const [userPassword, setUserPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loadingNaoVinculados, setLoadingNaoVinculados] = useState(false);
  const snackbar = useSnackbar();
  const hasFetchedPeople = useRef(false);
  const hasInitializedEdit = useRef(false);
  const pessoasVinculadosRef = useRef([]);
  const linkedPeoplePageRef = useRef([]);
  const displayedLinkedStoresRef = useRef([]);
  const loadedLinkedPageIdsRef = useRef(new Set());
  const peopleFetchSeqRef = useRef(0);
  const linkedFetchSeqRef = useRef(0);
  const lastLeftFetchKeyRef = useRef("");
  const lastRightFetchKeyRef = useRef("");
  const hasSetInitialLinkedRef = useRef(false);
  const hasLoadedLinkedPeopleRef = useRef(false);

  const filteredPeople = useMemo(() => {
    if (!people?.length) {
      return people;
    }

    const linkedForType = pessoasByType[accessPersonType] || [];

    if (!linkedForType.length) {
      return people;
    }

    return people.filter(
      (item) =>
        !linkedForType.some((linkedPerson) => linkedPerson.Id === item.Id),
    );
  }, [accessPersonType, pessoasByType, people]);

  pessoasVinculadosRef.current = pessoasVinculados;
  linkedPeoplePageRef.current = linkedPeoplePage;

  const selectedBrandsKey = useMemo(
    () =>
      brandFilter === BRAND_FILTER_ALL ? BRAND_FILTER_ALL : String(brandFilter),
    [brandFilter],
  );

  const enrichedPerfisVinculados = useMemo(() => {
    return perfisVinculados.map((perfil) => {
      const fullProfile = allProfiles.find(
        (item) => item.IdPerfil === perfil.IdPerfil,
      );

      if (!fullProfile) {
        return perfil;
      }

      return {
        ...perfil,
        Canais: fullProfile.Canais || perfil.Canais || [],
        TiposPessoa: fullProfile.TiposPessoa || perfil.TiposPessoa || [],
      };
    });
  }, [allProfiles, perfisVinculados]);

  const profileAccess = useMemo(
    () => aggregateProfileAccess(enrichedPerfisVinculados),
    [enrichedPerfisVinculados],
  );

  const visibleAccessTabs = useMemo(
    () => getVisibleAccessTabs(profileAccess.tiposPessoa),
    [profileAccess.tiposPessoa],
  );

  const profileAccessSummary = useMemo(
    () => formatProfileAccessSummary(profileAccess),
    [profileAccess],
  );

  const skipPersonSelection = useMemo(
    () => isInternoKsaOnly(profileAccess.tiposPessoa),
    [profileAccess.tiposPessoa],
  );

  // Perfis com o tipo Representante: a aba Lojista passa a mostrar só as lojas sem representante e as da
  // carteira dos representantes vinculados. Perfil só de lojista continua vendo as lojas normalmente.
  const hasRepresentativeTab = useMemo(
    () => visibleAccessTabs.some((tab) => tab.value === "REPRESENTANTE"),
    [visibleAccessTabs],
  );

  const representativeIdsKey = (pessoasByType.REPRESENTANTE || [])
    .map((person) => person.Id)
    .sort((a, b) => a - b)
    .join(",");

  const profileAccessKey = useMemo(
    () =>
      [
        profileAccess.canais.join(","),
        profileAccess.tiposPessoa.join(","),
      ].join("|"),
    [profileAccess],
  );

  const getSelectedBrandIds = () => {
    if (!brandFilter || brandFilter === BRAND_FILTER_ALL) {
      return "";
    }

    return String(brandFilter);
  };

  const fetchAllUserLinkedPeopleFromAdmin = async (idUsuario) => {
    const { Result } = await Api.get(
      "list-people-user",
      "adminportal",
      `?idUsuario=${idUsuario}`,
      true,
    );

    return Result;
  };

  const buildPessoasIdsForSubmit = async () => {
    const mergedLinkedPeople = mergePeopleByType(pessoasByType);

    if (formType === "create") {
      const ids = mergedLinkedPeople.map((person) => person.Id);

      return ids.length ? ids.join(",") : "";
    }

    if (!hasAccessChanges) {
      return null;
    }

    const ids = mergedLinkedPeople.map((person) => person.Id);

    return ids.length ? ids.join(",") : "";
  };

  const submit = async (values) => {
    const { nome, login, backoffice, keycloak, inativo, codigo } = values;
    const updatedUser = selectedUser || {};
    setSubmitting(true);

    let objectFinal;
    const verifyPassword = { Senha: `${userPassword}` };

    try {
      const pessoasIds = await buildPessoasIdsForSubmit();

      const editObject = {
        IdUsuario: updatedUser.IdUsuario,
        Nome: nome,
        Login: login,
        CodigoERP: codigo ?? "",
        Backoffice: backoffice.toString(),
        Keycloak: keycloak.toString(),
        Perfis: perfisVinculados?.map((item) => item.IdPerfil).join(","),
        Canais: canaisVinculados?.map((item) => item.IdCanal).join(","),
        Inativo: inativo,
        // O backend só confere a carteira quando o perfil tem o tipo Representante.
        ValidarCarteira: (hasRepresentativeTab && !backoffice).toString(),
      };

      if (pessoasIds !== null) {
        editObject.Pessoas = pessoasIds;
      }

      if (userPassword && formType === "edit") {
        objectFinal = Object.assign({}, editObject, verifyPassword);
      } else if (!userPassword && formType === "edit") {
        objectFinal = editObject;
      } else if (userPassword && formType === "create") {
        objectFinal = Object.assign({}, editObject, verifyPassword);
        delete objectFinal.IdUsuario;
      } else if (!userPassword && formType === "create") {
        objectFinal = editObject;
        delete objectFinal.IdUsuario;
      }

      if (formType === "create") {
        if (!objectFinal.Pessoas) {
          delete objectFinal.Pessoas;
        }

        if (perfisVinculados?.length === 0) {
          delete objectFinal.Perfis;
        }

        if (canaisVinculados?.length === 0) {
          delete objectFinal.Canais;
        }
      }

      if (formType === "edit") {
        const res = await Api.put(
          "edit-user-full",
          "adminportal",
          objectFinal,
          true,
        );

        if (res.Success) {
          snackbar.showMessage(`Usuário atualizado com sucesso!`, {
            severity: "success",
          });
          handleClose({ ...updatedUser, ...res.Result });
        } else {
          snackbar.showMessage(
            `Ocorreu um erro ao atualizar o usuário. Verifique os parâmetros informados.`,
            {
              severity: "error",
            },
          );
        }
        return;
      }

      if (formType === "create") {
        const res = await Api.post(
          "create-user-full",
          "adminportal",
          objectFinal,
          true,
        );

        if (res.Success) {
          snackbar.showMessage(`Usuário criado com sucesso!`, {
            severity: "success",
          });
          handleClose({ ...updatedUser, ...res.Result });
        } else {
          snackbar.showMessage(
            `Ocorreu um erro ao criar o usuário. Verifique os parâmetros informados.`,
            {
              severity: "error",
            },
          );
        }
      }
    } catch (error) {
      snackbar.showMessage(error, {
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      nome: selectedUser?.Nome || "",
      backoffice: selectedUser?.Backoffice || false,
      keycloak: selectedUser?.Keycloak ?? false,
      login: selectedUser?.Login || "",
      inativo: selectedUser?.Inativo || false,
      codigo: selectedUser?.CodigoErp || "",
    },
    onSubmit: submit,
  });

  const { values, handleChange, handleSubmit, dirty, resetForm } = formik;

  const handleClose = useCallback(
    (updatedUser) => {
      resetForm();
      setActiveStep(0);
      setConfirmPassword("");
      setUserPassword("");
      setSavingPassword(false);
      setDisablePasswordInput(formType === "edit");
      hasInitializedEdit.current = false;
      hasFetchedPeople.current = false;
      lastLeftFetchKeyRef.current = "";
      lastRightFetchKeyRef.current = "";
      loadedLinkedPageIdsRef.current = new Set();
      hasSetInitialLinkedRef.current = false;
      hasLoadedLinkedPeopleRef.current = false;
      setHasAccessChanges(false);
      setLinkedPeoplePage([]);
      setLinkedCurrentPage(0);
      setFilterLinkedStore(null);
      setFilterStore(null);
      setPessoasByType(createEmptyPeopleByType());
      setPessoasVinculados([]);
      peopleFetchSeqRef.current += 1;
      linkedFetchSeqRef.current += 1;
      onClose(updatedUser);
    },
    [formType, onClose, resetForm],
  );

  const isFormDisabled = submitting || loadingDetails || savingPassword;

  const handlePasswordEditClick = async () => {
    if (disablePasswordInput) {
      setDisablePasswordInput(false);
      setUserPassword("");
      setConfirmPassword("");
      return;
    }

    if (MaskAndValid.isNullOrEmpty(userPassword)) {
      snackbar.showMessage("Informe a nova senha do usuário.", {
        severity: "warning",
      });
      return;
    }

    if (userPassword !== confirmPassword) {
      snackbar.showMessage("As senhas informadas não conferem.", {
        severity: "warning",
      });
      return;
    }

    if (!selectedUser?.IdUsuario) {
      snackbar.showMessage(
        "Usuário não identificado para alteração de senha.",
        {
          severity: "error",
        },
      );
      return;
    }

    setSavingPassword(true);

    try {
      const res = await Api.put(
        "edit-user-full",
        "adminportal",
        {
          IdUsuario: selectedUser.IdUsuario,
          Senha: userPassword,
        },
        true,
      );

      if (res.Success) {
        snackbar.showMessage("Senha alterada com sucesso!", {
          severity: "success",
        });
        setDisablePasswordInput(true);
        setUserPassword("");
        setConfirmPassword("");
      } else {
        snackbar.showMessage(
          res.ValidationMessage ||
            "Ocorreu um erro ao alterar a senha. Verifique os parâmetros informados.",
          { severity: "error" },
        );
      }
    } catch (error) {
      snackbar.showMessage(error, {
        severity: "error",
      });
    } finally {
      setSavingPassword(false);
    }
  };

  const renderFieldSkeleton = () => (
    <Grid item xs={12} sm={6}>
      <Skeleton variant="text" width="35%" height={20} />
      <Skeleton variant="rect" height={40} style={{ borderRadius: 4 }} />
    </Grid>
  );

  const renderGeneralStepSkeleton = () => (
    <Grid container spacing={2}>
      {renderFieldSkeleton()}
      {renderFieldSkeleton()}
      {renderFieldSkeleton()}
      {renderFieldSkeleton()}
    </Grid>
  );

  const renderStepContentSkeleton = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Skeleton variant="text" width={120} height={32} />
        <Skeleton variant="rect" height={320} style={{ borderRadius: 4 }} />
      </Grid>
    </Grid>
  );

  const showConfirmPassword =
    formType === "create" || (formType === "edit" && !disablePasswordInput);

  const renderGeneralStep = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <FormGroup>
          <InputText
            label="Nome"
            required
            disabled={isFormDisabled}
            value={values.nome}
            onChange={handleChange}
            props={{
              name: "nome",
              onInput: (e) => {
                e.target.value = e.target.value.toString().slice(0, 200);
              },
            }}
          />
        </FormGroup>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormGroup>
          <InputText
            label="E-mail (Login)"
            required
            disabled={isFormDisabled}
            value={values.login}
            onChange={handleChange}
            placeholder="voce@dominio.com"
            type="email"
            props={{
              name: "login",
              onInput: (e) => {
                e.target.value = e.target.value.toString().slice(0, 200);
              },
            }}
          />
        </FormGroup>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormGroup
          style={{ display: "flex", justifyContent: "center", width: "100%" }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "end",
              width: "100%",
            }}
          >
            <PasswordField
              label="Senha"
              value={userPassword}
              disabled={
                formType === "edit"
                  ? disablePasswordInput || loadingDetails || savingPassword
                  : isFormDisabled
              }
              onChange={(e) => setUserPassword(e.target.value)}
              required={formType === "create"}
            />
            {formType === "edit" && (
              <Tooltip
                title={disablePasswordInput ? "Editar senha" : "Salvar senha"}
              >
                <span>
                  <IconButton
                    aria-label={
                      disablePasswordInput ? "edit-password" : "save-password"
                    }
                    disabled={loadingDetails || savingPassword}
                    onClick={handlePasswordEditClick}
                    color={disablePasswordInput ? "default" : "primary"}
                  >
                    {savingPassword ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Box
                        component={disablePasswordInput ? Create : Check}
                        width="1.25rem!important"
                        height="1.25rem!important"
                      />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </FormGroup>
      </Grid>
      {showConfirmPassword && (
        <Grid item xs={12} sm={6}>
          <FormGroup>
            <PasswordField
              label="Confirmar senha"
              value={confirmPassword}
              disabled={isFormDisabled}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required={formType === "create" || !disablePasswordInput}
            />
          </FormGroup>
        </Grid>
      )}
      {formType === "edit" && isRepresentativeSelected && (
        <Grid item xs={12} sm={6}>
          <FormGroup>
            <InputText
              label="Código ERP"
              disabled={isFormDisabled}
              value={values.codigo}
              onChange={handleChange}
              props={{
                name: "codigo",
                onInput: (e) => {
                  e.target.value = e.target.value.toString().slice(0, 10);
                },
              }}
            />
          </FormGroup>
        </Grid>
      )}
      {formType === "edit" && (
        <Grid item xs={12} style={{ display: "flex", alignItems: "center" }}>
          <FormGroup row style={{ marginBottom: 0 }}>
            {isAdmin && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.backoffice}
                    onChange={(event) => {
                      if (handleBackofficeToggleGuard(event)) {
                        handleChange(event);
                      }
                    }}
                    name="backoffice"
                    color="primary"
                    disabled={isFormDisabled}
                  />
                }
                label={<FormLabel>Backoffice</FormLabel>}
              />
            )}
            {isAdmin && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.keycloak}
                    onChange={handleChange}
                    name="keycloak"
                    color="primary"
                    disabled={isFormDisabled}
                  />
                }
                label={<FormLabel>Keycloak</FormLabel>}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.inativo}
                  onChange={handleChange}
                  name="inativo"
                  color="primary"
                  disabled={isFormDisabled}
                />
              }
              label={<FormLabel>Inativo</FormLabel>}
            />
          </FormGroup>
        </Grid>
      )}
    </Grid>
  );

  const validateGeneralStep = () => {
    if (MaskAndValid.isNullOrEmpty(values.nome)) {
      snackbar.showMessage("Informe o nome do usuário.", {
        severity: "warning",
      });
      return false;
    }

    if (
      MaskAndValid.isNullOrEmpty(values.login) ||
      !MaskAndValid.validateEmail(values.login)
    ) {
      snackbar.showMessage("Informe um e-mail válido.", {
        severity: "warning",
      });
      return false;
    }

    const requiresPassword =
      formType === "create" || (formType === "edit" && !disablePasswordInput);

    if (requiresPassword && MaskAndValid.isNullOrEmpty(userPassword)) {
      snackbar.showMessage("Informe a senha do usuário.", {
        severity: "warning",
      });
      return false;
    }

    if (requiresPassword && userPassword !== confirmPassword) {
      snackbar.showMessage("As senhas informadas não conferem.", {
        severity: "warning",
      });
      return false;
    }

    return true;
  };

  const validateProfileStep = () => {
    if (formType === "create" && perfisVinculados?.length === 0) {
      snackbar.showMessage("Selecione ao menos um perfil.", {
        severity: "warning",
      });
      return false;
    }

    if (enrichedPerfisVinculados.some((perfil) => !perfil.Canais?.length)) {
      if (allProfiles.length > 0) {
        snackbar.showMessage(
          "Um ou mais perfis selecionados não possuem canal configurado.",
          { severity: "warning" },
        );
        return false;
      }
    }

    if (
      enrichedPerfisVinculados.some((perfil) => !perfil.TiposPessoa?.length)
    ) {
      if (allProfiles.length > 0) {
        snackbar.showMessage(
          "Um ou mais perfis selecionados não possuem tipo de pessoa configurado.",
          { severity: "warning" },
        );
        return false;
      }
    }

    return true;
  };

  const validateAccessStepForm = () => {
    const message = validateAccessStep({
      profileAccess,
      peopleByType: pessoasByType,
    });

    if (message) {
      snackbar.showMessage(message, { severity: "warning" });
      return false;
    }

    return true;
  };

  const handleNextStep = () => {
    if (activeStep === 0 && !validateGeneralStep()) {
      return;
    }

    if (activeStep === 1 && !validateProfileStep()) {
      return;
    }

    setActiveStep((current) => Math.min(current + 1, 2));
  };

  const handlePreviousStep = () => {
    setActiveStep((current) => Math.max(current - 1, 0));
  };

  const renderProfilesTransferList = () => (
    <>
      <TransferList
        loading={!profiles}
        initialLeft={profiles}
        initialRight={perfisVinculados}
        leftLabel="Perfis disponíveis"
        rightLabel="Perfis vinculados"
        onRightChange={(value) =>
          value &&
          !UtilsFncs.ArrayEquals(value, perfisVinculados) &&
          setPerfisVinculados(value)
        }
        itemLabelKey="Nome"
        itemIdKey="IdPerfil"
        checked={preSelectedProfiles}
        setChecked={setPreSelectedProfiles}
      />
      {enrichedPerfisVinculados.length > 0 && (
        <Box mt={2}>
          {enrichedPerfisVinculados.map((perfil) => (
            <Box key={perfil.IdPerfil} mb={1}>
              <Typography variant="subtitle2">{perfil.Nome}</Typography>
              <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                {(perfil.Canais || []).map((canal) => (
                  <Typography
                    key={`${perfil.IdPerfil}-${canal}`}
                    variant="caption"
                    color="textSecondary"
                  >
                    {getProfileOptionLabel(PROFILE_CANAL_OPTIONS, canal)}
                  </Typography>
                ))}
                {(perfil.TiposPessoa || []).map((tipo) => (
                  <Typography
                    key={`${perfil.IdPerfil}-${tipo}`}
                    variant="caption"
                    color="primary"
                  >
                    {getProfileOptionLabel(PROFILE_TIPO_PESSOA_OPTIONS, tipo)}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </>
  );

  // Ids das lojas na carteira dos representantes informados (só quem tem linha ativa em lojista_representante).
  const fetchCarteiraStoreIds = async (representativeIds) => {
    if (!representativeIds.length) {
      return new Set();
    }

    const { Success, Result } = await Api.get(
      "store-user",
      "commercialstructure",
      `?apenasCarteira=true&apenasIds=true&idsRepresentante=${representativeIds.join(",")}&page=0&pageSize=1`,
      true,
    );

    return new Set(Success ? (Result?.ListLojas ?? []).map((loja) => loja.Id) : []);
  };

  // Desvincular um representante desvincula do usuário as lojas que vieram da carteira dele, exceto as que
  // também estão na carteira de outro representante ainda vinculado. Lojas sem representante não são afetadas.
  const removeStoresOfRemovedRepresentatives = async (removedIds, remainingIds) => {
    try {
      const [removedCarteira, remainingCarteira] = await Promise.all([
        fetchCarteiraStoreIds(removedIds),
        fetchCarteiraStoreIds(remainingIds),
      ]);
      const toRemove = new Set(
        [...removedCarteira].filter((id) => !remainingCarteira.has(id)),
      );

      if (!toRemove.size) {
        return;
      }

      let removedCount = 0;

      setPessoasByType((previous) => {
        const lojistas = (previous.LOJISTA || []).filter(
          (person) => !toRemove.has(person.Id),
        );
        removedCount = (previous.LOJISTA || []).length - lojistas.length;
        const updated = { ...previous, LOJISTA: lojistas };
        const merged = mergePeopleByType(updated);
        pessoasVinculadosRef.current = merged;
        setPessoasVinculados(merged);
        return updated;
      });
      setLinkedPeoplePage((previous) =>
        previous.filter((person) => !toRemove.has(person.Id)),
      );
      setHasAccessChanges(true);

      if (removedCount > 0) {
        snackbar.showMessage(
          `${removedCount} loja(s) da carteira do representante removido também foram desvinculadas.`,
          { severity: "info" },
        );
      }
    } catch (error) {
      snackbar.showMessage(error, { severity: "error" });
    }
  };

  // Ao escolher lojas, os representantes delas são vinculados junto, para nenhuma loja ficar sem o seu
  // representante. A consulta devolve só os representantes da carteira ativa das lojas informadas.
  const linkRepresentativesOfStores = async (storeIds) => {
    try {
      const representatives = new Map();
      const chunkSize = 200;

      for (let i = 0; i < storeIds.length; i += chunkSize) {
        const chunk = storeIds.slice(i, i + chunkSize);
        const { Success, Result } = await Api.get(
          "store-user",
          "commercialstructure",
          `?representantesDasLojas=${chunk.join(",")}&page=0&pageSize=1`,
          true,
        );

        if (!Success) {
          snackbar.showMessage(
            "Não foi possível buscar os representantes das lojas escolhidas.",
            { severity: "error" },
          );
          return;
        }

        (Result?.ListLojas ?? []).forEach((person) => {
          representatives.set(person.Id, mapPersonToListItem(person));
        });
      }

      if (!representatives.size) {
        snackbar.showMessage(
          "As lojas escolhidas não têm representante ativo na carteira.",
          { severity: "info" },
        );
        return;
      }

      let addedCount = 0;

      setPessoasByType((previous) => {
        const current = previous.REPRESENTANTE || [];
        const currentIds = new Set(current.map((person) => person.Id));
        const toAdd = [...representatives.values()].filter(
          (person) => !currentIds.has(person.Id),
        );
        addedCount = toAdd.length;

        if (!toAdd.length) {
          return previous;
        }

        const updated = { ...previous, REPRESENTANTE: [...current, ...toAdd] };
        const merged = mergePeopleByType(updated);
        pessoasVinculadosRef.current = merged;
        setPessoasVinculados(merged);
        return updated;
      });

      if (addedCount > 0) {
        setHasAccessChanges(true);
        snackbar.showMessage(
          `${addedCount} representante(s) das lojas escolhidas foram vinculados automaticamente.`,
          { severity: "info" },
        );
      }
    } catch (error) {
      snackbar.showMessage(error, { severity: "error" });
    }
  };

  const handlePessoasVinculadosChange = useCallback(
    (value) => {
      if (!value) {
        return;
      }

      if (accessPersonType === "LOJISTA" && hasRepresentativeTab) {
        const currentStoreIds = new Set(
          (pessoasByType.LOJISTA || []).map((person) => person.Id),
        );
        const addedStoreIds = value
          .map((person) => person.Id)
          .filter((id) => !currentStoreIds.has(id));

        if (addedStoreIds.length > 0) {
          linkRepresentativesOfStores(addedStoreIds);
        }
      }

      if (accessPersonType === "REPRESENTANTE" && hasRepresentativeTab) {
        const nextIds = new Set(value.map((person) => person.Id));
        const removedIds = (pessoasByType.REPRESENTANTE || [])
          .map((person) => person.Id)
          .filter((id) => !nextIds.has(id));

        if (removedIds.length > 0) {
          removeStoresOfRemovedRepresentatives(removedIds, [...nextIds]);
        }
      }

      // Em edição a lista da direita é a página salva no servidor + o que foi adicionado nesta sessão.
      const pageIds = new Set(
        displayedLinkedStoresRef.current.map((person) => person.Id),
      );
      const currentType = accessPersonType;
      const master = pessoasByType[currentType] || [];
      let next = value;

      if (
        formType === "edit" &&
        pageIds.size > 0 &&
        currentType === "LOJISTA"
      ) {
        const withoutCurrentPage = master.filter(
          (person) => !pageIds.has(person.Id),
        );
        next = [...withoutCurrentPage, ...value];
      }

      if (arePeopleListsEqual(next, master)) {
        return;
      }

      setPessoasByType((previous) => {
        const updated = {
          ...previous,
          [currentType]: next,
        };
        const merged = mergePeopleByType(updated);
        pessoasVinculadosRef.current = merged;
        setPessoasVinculados(merged);
        return updated;
      });
      setHasAccessChanges(true);
    },
    [accessPersonType, formType, hasRepresentativeTab, pessoasByType],
  );

  // Em edição, a lista de lojas vinculadas vem paginada do servidor e só conhece o que já estava salvo. Sem somar
  // o que foi adicionado nesta sessão (e sem tirar o que foi removido), trocar de aba fazia a loja escolhida sumir
  // da lista, embora continuasse no estado e fosse enviada ao salvar.
  const displayedLinkedStores = useMemo(() => {
    const master = pessoasByType.LOJISTA || [];

    if (formType !== "edit") {
      return master;
    }

    const masterIds = new Set(master.map((person) => person.Id));
    const savedIds = new Set(
      (initialPessoasVinculados || []).map((person) => person.Id),
    );
    const pageIds = new Set(linkedPeoplePage.map((person) => person.Id));
    const keptFromPage = linkedPeoplePage.filter((person) =>
      masterIds.has(person.Id),
    );
    const addedThisSession = master.filter(
      (person) => !savedIds.has(person.Id) && !pageIds.has(person.Id),
    );

    return [...keptFromPage, ...addedThisSession];
  }, [formType, pessoasByType.LOJISTA, linkedPeoplePage, initialPessoasVinculados]);

  displayedLinkedStoresRef.current = displayedLinkedStores;

  const currentLinkedPeople =
    formType === "edit" && accessPersonType === "LOJISTA"
      ? displayedLinkedStores
      : pessoasByType[accessPersonType] || [];

  const rightListItems = currentLinkedPeople;

  const renderAccessStep = () => (
    <UserAccessSection
      allBrands={allBrands}
      brandFilter={brandFilter}
      onBrandFilterChange={handleBrandFilterChange}
      accessPersonType={accessPersonType}
      onAccessPersonTypeChange={handleAccessPersonTypeChange}
      visibleAccessTabs={visibleAccessTabs}
      profileAccessSummary={profileAccessSummary}
      showBackofficeSwitch={formType === "edit" && isAdmin}
      backoffice={values.backoffice}
      onBackofficeChange={handleBackofficeChange}
      disabled={isFormDisabled}
      skipPersonSelection={skipPersonSelection}
      filteredPeople={filteredPeople}
      pessoasVinculados={rightListItems}
      onPessoasVinculadosChange={handlePessoasVinculadosChange}
      loadingNaoVinculados={loadingNaoVinculados}
      loadingLinkedPeople={loadingLinkedPeople}
      filterStore={filterStore}
      setFilterStore={setFilterStore}
      filterLinkedStore={filterLinkedStore}
      setFilterLinkedStore={setFilterLinkedStore}
      totalRegistros={totalRegistros}
      currentPage={Number(currentPage)}
      setCurrentPage={setCurrentPage}
      totalPage={totalPage}
      linkedCurrentPage={Number(linkedCurrentPage)}
      setLinkedCurrentPage={setLinkedCurrentPage}
      linkedTotalPage={linkedTotalPage}
      linkedTotalRegistros={linkedTotalRegistros}
      preSelectedPeople={preSelectedPeople}
      setPreSelectedPeople={setPreSelectedPeople}
      formType={formType}
    />
  );

  const handleBrandFilterChange = (value) => {
    setBrandFilter(value);
    setCurrentPage(0);
    setLinkedCurrentPage(0);
  };

  const handleAccessPersonTypeChange = (value) => {
    if (value !== accessPersonType) {
      setAccessPersonType(value);
      setCurrentPage(0);
      setLinkedCurrentPage(0);
    }
  };

  // Representante nunca é backoffice: o backoffice enxerga tudo porque quem é representante sempre segue o vínculo.
  const isRepresentativeUser = () =>
    isRepresentativeSelected || (pessoasByType.REPRESENTANTE || []).length > 0;

  const handleBackofficeToggleGuard = (event) => {
    if (event.target.checked && isRepresentativeUser()) {
      snackbar.showMessage(
        "Um usuário representante não pode ser backoffice. Remova o perfil ou os representantes vinculados antes.",
        { severity: "warning" },
      );
      return false;
    }

    return true;
  };

  const handleBackofficeChange = (event) => {
    if (!handleBackofficeToggleGuard(event)) {
      return;
    }

    handleChange({
      target: {
        name: "backoffice",
        type: "checkbox",
        checked: event.target.checked,
      },
    });
    setCurrentPage(0);
    setLinkedCurrentPage(0);
  };

  const mergeLinkedPageIntoMaster = (pageItems) => {
    pageItems.forEach((person) => {
      loadedLinkedPageIdsRef.current.add(person.Id);
    });

    setPessoasVinculados((previous) => {
      const byId = new Map(previous.map((person) => [person.Id, person]));

      pageItems.forEach((person) => {
        byId.set(person.Id, person);
      });

      const merged = Array.from(byId.values());
      pessoasVinculadosRef.current = merged;
      return merged;
    });

    setPessoasByType((previous) => {
      const byId = new Map(
        (previous.LOJISTA || []).map((person) => [person.Id, person]),
      );

      pageItems.forEach((person) => {
        byId.set(person.Id, person);
      });

      return {
        ...previous,
        LOJISTA: Array.from(byId.values()),
      };
    });
  };

  const fetchProfiles = useCallback(() => {
    return Api.get(
      "list-profile",
      "adminportal",
      "?situacaoPerfil=ativos&limit=100",
      true,
    )
      .then(({ Success, Result }) => {
        if (Success) {
          setAllProfiles(Result || []);

          if (Result.length === 0) {
            setProfiles([]);
          } else if (!perfisVinculados) {
            setProfiles(Result);
            setPerfisVinculados([]);
          } else {
            const naoVinculados = filtraNaoVinculados(
              Result,
              perfisVinculados,
              "IdPerfil",
            );
            setProfiles(naoVinculados);
          }
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
      });
  }, [perfisVinculados, snackbar]);

  const fetchChannels = useCallback(async () => {
    try {
      const result = await getChannels();
      const Result = result?.data ?? [];
      if (Result.length === 0) {
        setChannels([]);
      } else if (!canaisVinculados) {
        setChannels(Result);
        setCanaisVinculados([]);
      } else {
        const naoVinculados = filtraNaoVinculados(
          Result,
          canaisVinculados,
          "IdCanal",
        );
        setChannels(naoVinculados);
      }
    } catch (error) {
      snackbar.showMessage(error, {
        severity: "error",
      });
    }
  }, [canaisVinculados, snackbar, getChannels]);

  const fetchBrands = useCallback(async () => {
    try {
      const result = await getBrands();
      const Result = result?.data ?? [];
      const newListBrands = Result.filter((brand) => brand?.IdMarca !== 0).map(
        (brand) => ({
          IdMarca: brand?.IdMarca,
          Nome: brand?.Nome,
          Sigla: brand?.Sigla,
          UrlSite: brand?.UrlSite,
        }),
      );

      setAllBrands(newListBrands);
      setBrands(newListBrands);
    } catch (error) {
      snackbar.showMessage(error, {
        severity: "error",
      });
    }
  }, [snackbar, getBrands]);

  const fetchPeople = useCallback(async () => {
    const seq = ++peopleFetchSeqRef.current;
    setLoadingNaoVinculados(true);

    const linkedPeople = pessoasByType[accessPersonType] || [];
    const brandIds = getSelectedBrandIds();
    const brandParam = brandIds ? `&idMarca=${brandIds}` : "";
    const canaisPerfil = getCanaisPerfilParam(profileAccess.canais);
    const canaisParam = canaisPerfil ? `&canaisPerfil=${canaisPerfil}` : "";
    // Representante primeiro: a aba Lojista oferece só a carteira dos representantes já vinculados (sem as lojas
    // "livres", que hoje são quase toda a base antiga de clientes) e só lojas ativas.
    const carteiraParam = hasRepresentativeTab
      ? `&escopoCarteira=true&incluirSemRepresentante=false&idsRepresentante=${representativeIdsKey}`
      : "";

    try {
      if (accessPersonType === "REPRESENTANTE") {
        const representativeParams = brandIds ? { idMarca: brandIds } : {};
        const result = await getRepresentatives(representativeParams);

        if (seq !== peopleFetchSeqRef.current) {
          return;
        }

        const mappedPeople = (result?.data ?? []).map(mapPersonToListItem);
        const filteredPeople = mappedPeople.filter((person) =>
          matchesPersonSearchFilter(person, filterStore),
        );
        const naoVinculados = filtraNaoVinculados(
          filteredPeople,
          linkedPeople,
          "Id",
        );
        const start = currentPage * ACCESS_PAGE_SIZE;
        const pageItems = naoVinculados.slice(start, start + ACCESS_PAGE_SIZE);

        setPeople(pageItems);
        setTotalRegistros(naoVinculados.length);
        setTotalPage(Math.ceil(naoVinculados.length / ACCESS_PAGE_SIZE) || 0);
        return;
      }

      const { Success, Result } = await Api.get(
        "store-user",
        "commercialstructure",
        `?page=${currentPage}&filtro=${
          filterStore ? filterStore : ""
        }&pageSize=${ACCESS_PAGE_SIZE}${brandParam}${canaisParam}${carteiraParam}`,
        true,
      );

      if (seq !== peopleFetchSeqRef.current) {
        return;
      }

      if (!Success) {
        snackbar.showMessage(
          "Não foi possível carregar as lojas disponíveis. Tente novamente.",
          { severity: "error" },
        );
        return;
      }

      if (Result.ListLojas.length === 0) {
        setPeople([]);
        setTotalPage(0);
        setTotalRegistros(0);
        return;
      }

      const naoVinculados = filtraNaoVinculados(
        Result.ListLojas,
        linkedPeople,
        "Id",
      );
      const peoplesList = naoVinculados.map(mapPersonToListItem);
      const linkedCount = linkedPeople.length;

      setPeople(peoplesList);
      setTotalRegistros(Math.max(0, Result.TotalRegistros - linkedCount));
      setTotalPage(
        Math.ceil(
          Math.max(0, Result.TotalRegistros - linkedCount) / ACCESS_PAGE_SIZE,
        ) || 0,
      );
    } catch (error) {
      if (seq !== peopleFetchSeqRef.current) {
        return;
      }

      snackbar.showMessage(error, {
        severity: "error",
      });
    } finally {
      if (seq === peopleFetchSeqRef.current) {
        hasFetchedPeople.current = true;
        setLoadingNaoVinculados(false);
      }
    }
  }, [
    accessPersonType,
    currentPage,
    filterStore,
    getRepresentatives,
    brandFilter,
    profileAccess.canais,
    hasRepresentativeTab,
    representativeIdsKey,
    snackbar,
  ]);

  const loadUserLinkedPeople = useCallback(async () => {
    if (!selectedUser?.IdUsuario) {
      return;
    }

    setLoadingLinkedPeople(true);

    try {
      const allLinkedPeople = await fetchAllUserLinkedPeopleFromAdmin(
        selectedUser.IdUsuario,
      );
      const byType = splitLinkedPeopleByType(allLinkedPeople);
      const merged = mergePeopleByType(byType);

      setPessoasByType(byType);
      pessoasVinculadosRef.current = merged;
      setPessoasVinculados(merged);
      setInitialPessoasVinculados(merged);
      loadedLinkedPageIdsRef.current = new Set(
        merged.map((person) => person.Id),
      );
      hasSetInitialLinkedRef.current = true;
      hasLoadedLinkedPeopleRef.current = true;
    } catch (error) {
      snackbar.showMessage(error, {
        severity: "error",
      });
    } finally {
      setLoadingLinkedPeople(false);
    }
  }, [selectedUser?.IdUsuario, snackbar]);

  const fetchLinkedPeoplePage = useCallback(async () => {
    const seq = ++linkedFetchSeqRef.current;
    setLoadingLinkedPeople(true);

    const brandIds = getSelectedBrandIds();
    const brandParam = brandIds ? `&idMarca=${brandIds}` : "";

    try {
      const { Success, Result } = await Api.get(
        "store-user",
        "commercialstructure",
        `?page=${linkedCurrentPage}&filtro=${
          filterLinkedStore ? filterLinkedStore : ""
        }&pageSize=${ACCESS_PAGE_SIZE}${brandParam}&idUsuario=${selectedUser.IdUsuario}`,
        true,
      );

      if (seq !== linkedFetchSeqRef.current) {
        return;
      }

      if (!Success) {
        return;
      }

      const pageItems = (Result?.ListLojas ?? []).map(mapPersonToListItem);

      setLinkedPeoplePage(pageItems);
      setLinkedTotalRegistros(Result?.TotalRegistros ?? 0);
      setLinkedTotalPage(Number(Result?.TotalDePaginas ?? 0));

      if (pageItems.length > 0) {
        mergeLinkedPageIntoMaster(pageItems);

        if (!hasSetInitialLinkedRef.current) {
          hasSetInitialLinkedRef.current = true;
          setInitialPessoasVinculados(pageItems);
        }
      }
    } catch (error) {
      if (seq !== linkedFetchSeqRef.current) {
        return;
      }

      snackbar.showMessage(error, {
        severity: "error",
      });
    } finally {
      if (seq === linkedFetchSeqRef.current) {
        setLoadingLinkedPeople(false);
      }
    }
  }, [
    filterLinkedStore,
    linkedCurrentPage,
    brandFilter,
    selectedUser?.IdUsuario,
    snackbar,
  ]);

  const handleVerifyProfile = () => {
    const userLoggedProfile = JSON.parse(
      localStorage.getItem("userDetail"),
    ).UsuarioPerfis;
    const userLoggedProfileVision = JSON.parse(
      localStorage.getItem("userDetailVision"),
    )?.UsuarioPerfis;
    const userProfile = userLoggedProfileVision
      ? userLoggedProfileVision
      : userLoggedProfile;

    if (userHasAnyProfileName(userProfile, ["ADM"])) {
      setIsAdmin(true);
    }
  };

  const handleVerifySelectedProfiles = () => {
    setIsRepresentativeSElected(userIsRepresentante(enrichedPerfisVinculados));
  };

  useEffect(() => {
    handleVerifyProfile();
  }, []);

  useEffect(() => {
    if (formType !== "create") {
      return;
    }

    Promise.all([
      !profiles || profiles.length === 0 ? fetchProfiles() : null,
      !channels || channels.length === 0 ? fetchChannels() : null,
      !brands || brands.length === 0 ? fetchBrands() : null,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType]);

  useEffect(() => {
    if (
      formType !== "edit" ||
      loadingDetails ||
      !selectedUser?.Login ||
      hasInitializedEdit.current
    ) {
      return;
    }

    resetForm({
      values: {
        nome: selectedUser.Nome || "",
        backoffice: selectedUser.Backoffice || false,
        keycloak: selectedUser.Keycloak ?? false,
        login: selectedUser.Login || "",
        inativo: selectedUser.Inativo || false,
        codigo: selectedUser.CodigoErp || "",
      },
    });

    setPerfisVinculados(selectedUser.UsuarioPerfis || []);
    setCanaisVinculados(selectedUser.UsuarioCanais || []);
    setPreSelectedProfiles(selectedUser.UsuarioPerfis || []);
    setPreSelectedChannels(selectedUser.UsuarioCanais || []);

    hasInitializedEdit.current = true;

    Promise.all([fetchProfiles(), fetchChannels(), fetchBrands()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, loadingDetails, selectedUser?.IdUsuario, selectedUser?.Login]);

  useEffect(() => {
    if (!visibleAccessTabs.length) {
      return;
    }

    const isCurrentTabVisible = visibleAccessTabs.some(
      (tab) => tab.value === accessPersonType,
    );

    if (!isCurrentTabVisible) {
      setAccessPersonType(visibleAccessTabs[0].value);
      setCurrentPage(0);
      setLinkedCurrentPage(0);
    }
  }, [accessPersonType, visibleAccessTabs]);

  useEffect(() => {
    if (activeStep !== 2) {
      lastLeftFetchKeyRef.current = "";
      lastRightFetchKeyRef.current = "";
      return;
    }
  }, [activeStep]);

  useEffect(() => {
    if (activeStep !== 2 || loadingDetails || skipPersonSelection) {
      return;
    }

    if (!visibleAccessTabs.length) {
      return;
    }

    const leftFetchKey = [
      currentPage,
      filterStore || "",
      accessPersonType,
      selectedBrandsKey,
      profileAccessKey,
      values.backoffice,
      accessPersonType === "LOJISTA" ? representativeIdsKey : "",
    ].join("|");

    if (lastLeftFetchKeyRef.current === leftFetchKey) {
      return;
    }

    lastLeftFetchKeyRef.current = leftFetchKey;

    let cancelled = false;

    async function loadAvailablePeople() {
      await fetchPeople();

      if (cancelled) {
        return;
      }
    }

    loadAvailablePeople();

    return () => {
      cancelled = true;
      peopleFetchSeqRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeStep,
    loadingDetails,
    skipPersonSelection,
    visibleAccessTabs.length,
    brandFilter,
    currentPage,
    filterStore,
    accessPersonType,
    selectedBrandsKey,
    profileAccessKey,
    values.backoffice,
    representativeIdsKey,
  ]);

  useEffect(() => {
    if (
      activeStep !== 2 ||
      loadingDetails ||
      formType !== "edit" ||
      !selectedUser?.IdUsuario ||
      hasLoadedLinkedPeopleRef.current
    ) {
      return;
    }

    loadUserLinkedPeople();
  }, [
    activeStep,
    formType,
    loadingDetails,
    loadUserLinkedPeople,
    selectedUser?.IdUsuario,
  ]);

  useEffect(() => {
    if (
      activeStep !== 2 ||
      loadingDetails ||
      formType !== "edit" ||
      !selectedUser?.IdUsuario ||
      accessPersonType !== "LOJISTA" ||
      !hasLoadedLinkedPeopleRef.current
    ) {
      return;
    }

    const rightFetchKey = [
      linkedCurrentPage,
      filterLinkedStore || "",
      selectedBrandsKey,
    ].join("|");

    if (lastRightFetchKeyRef.current === rightFetchKey) {
      return;
    }

    lastRightFetchKeyRef.current = rightFetchKey;

    let cancelled = false;

    async function loadLinkedPeople() {
      await fetchLinkedPeoplePage();

      if (cancelled) {
        return;
      }
    }

    loadLinkedPeople();

    return () => {
      cancelled = true;
      linkedFetchSeqRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeStep,
    loadingDetails,
    formType,
    selectedUser?.IdUsuario,
    accessPersonType,
    linkedCurrentPage,
    filterLinkedStore,
    selectedBrandsKey,
  ]);

  useEffect(() => {
    handleVerifySelectedProfiles();
  }, [enrichedPerfisVinculados]);

  const canSubmitEdit =
    !loadingDetails &&
    !submitting &&
    !savingPassword &&
    !(!disablePasswordInput && MaskAndValid.isNullOrEmpty(userPassword)) &&
    !(
      checkNothingChanged(
        selectedUser,
        perfisVinculados,
        canaisVinculados,
        initialPessoasVinculados,
        hasAccessChanges ? pessoasVinculados : initialPessoasVinculados,
        userPassword,
      ) &&
      formType === "edit" &&
      !dirty &&
      !hasAccessChanges
    ) &&
    !MaskAndValid.isNullOrEmpty(values.nome) &&
    !MaskAndValid.isNullOrEmpty(values.login) &&
    MaskAndValid.validateEmail(values.login);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      style={{ height: "100%" }}
    >
      <Box style={{ height: "80vh", display: "flex", flexDirection: "column" }}>
        <DialogTitle>
          <Typography id="alert-dialog-title" variant="h3" component="h3">
            {`${formType === "edit" ? "Editar" : "Criar"} Usuário`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <UserFormStepper currentStep={activeStep} />

          {loadingDetails && (
            <Box mb={2}>
              <LinearProgress />
            </Box>
          )}

          {activeStep === 0 &&
            (loadingDetails
              ? renderGeneralStepSkeleton()
              : renderGeneralStep())}

          {activeStep === 1 &&
            (loadingDetails ? (
              renderStepContentSkeleton()
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h4">Perfil</Typography>
                  {renderProfilesTransferList()}
                </Grid>
              </Grid>
            ))}

          {activeStep === 2 &&
            (loadingDetails ? (
              renderStepContentSkeleton()
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h4">Acessos</Typography>
                  {renderAccessStep()}
                </Grid>
              </Grid>
            ))}

          {submitting && (
            <div style={{ marginTop: "1rem" }}>
              <span>{`${
                formType === "create" ? "Criando" : "Atualizando"
              } usuário...`}</span>
              <LinearProgress />
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ display: "flex", justifyContent: "start" }}>
          <Button component={Box} onClick={handleClose} disabled={submitting}>
            Cancelar
          </Button>
          {activeStep > 0 && (
            <Button
              color="default"
              variant="outlined"
              disabled={isFormDisabled}
              onClick={handlePreviousStep}
            >
              Voltar
            </Button>
          )}
          {activeStep < 2 && (
            <Button
              color="default"
              variant="contained"
              disabled={isFormDisabled}
              onClick={handleNextStep}
            >
              Avançar
            </Button>
          )}
          {activeStep === 2 && formType === "create" && (
            <Button
              color="default"
              variant="contained"
              type="submit"
              disabled={
                isFormDisabled ||
                MaskAndValid.isNullOrEmpty(values.nome) ||
                MaskAndValid.isNullOrEmpty(values.login) ||
                !MaskAndValid.validateEmail(values.login) ||
                MaskAndValid.isNullOrEmpty(userPassword) ||
                userPassword !== confirmPassword ||
                perfisVinculados?.length === 0
              }
              onClick={() => {
                if (
                  !validateGeneralStep() ||
                  !validateProfileStep() ||
                  !validateAccessStepForm()
                ) {
                  return;
                }

                handleSubmit();
              }}
            >
              Criar
            </Button>
          )}
          {activeStep === 2 && formType === "edit" && (
            <Button
              color="default"
              variant="contained"
              type="submit"
              disabled={!canSubmitEdit}
              onClick={() => {
                if (
                  !validateGeneralStep() ||
                  !validateProfileStep() ||
                  !validateAccessStepForm()
                ) {
                  return;
                }

                handleSubmit();
              }}
            >
              Atualizar
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
